import { useState, useEffect } from "react"
import { HelpCircle, X, Save, Loader2, Clock, Award, Plus, Trash2, ArrowUp, ArrowDown, Languages } from "lucide-react"
import { Button } from "../ui/button"
import { apiClient } from "../../utils/apiComponent/apiClient"
import { endpoints } from "../../utils/endpoints"
import { toast } from "react-toastify"

const QUESTION_TYPES = [
  { value: 'SINGLE_CHOICE', label: 'Choix unique' },
  { value: 'MULTIPLE_CHOICE', label: 'Choix multiple' },
  { value: 'TEXT', label: 'Réponse textuelle' },
  { value: 'TRUE_FALSE', label: 'Vrai/Faux' },
  { value: 'ESSAY', label: 'Essai' },
  { value: 'NUMERIC', label: 'Réponse numérique' },
  { value: 'FILL_IN_THE_BLANK', label: 'Remplir les blancs' },
  { value: 'MATCHING', label: 'Association' }
]

export default function QuizForm({
  quiz = null, // null pour création, objet pour édition
  onSave,
  onCancel,
  onQuestionsCreated, // Callback appelé après la création des questions
  onShowTranslations
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: 'Répondez à toutes les questions.',
    timeLimit: 30,
    passingScore: 70,
    maxAttempts: null, // null = tentatives illimitées
    isRandomized: false,
    isPublished: true
  })
  const [questions, setQuestions] = useState([])
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    type: 'SINGLE_CHOICE',
    points: 1,
    order: 1,
    isRequired: true,
    options: [
      { text: '', isCorrect: false, order: 1 },
      { text: '', isCorrect: false, order: 2 }
    ]
  })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [creatingQuestions, setCreatingQuestions] = useState(false)
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false)
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null)

  useEffect(() => {
    if (quiz) {
      setFormData({
        title: quiz.title || '',
        description: quiz.description || '',
        instructions: quiz.instructions || 'Répondez à toutes les questions.',
        timeLimit: quiz.timeLimit || 30,
        passingScore: quiz.passingScore || 70,
        maxAttempts: quiz.maxAttempts ?? null, // null = tentatives illimitées
        isRandomized: quiz.isRandomized || false,
        isPublished: quiz.isPublished || false
      })
      // Charger les questions existantes si le quiz existe
      if (quiz.questions && Array.isArray(quiz.questions)) {
        setQuestions(quiz.questions.map((q, index) => ({
          ...q,
          order: index + 1
        })))
      }
    } else {
      // Réinitialiser pour un nouveau quiz
      setFormData({
        title: '',
        description: '',
        instructions: 'Répondez à toutes les questions.',
        timeLimit: 30,
        passingScore: 70,
        maxAttempts: null, // null = tentatives illimitées
        isRandomized: false,
        isPublished: true
      })
      setQuestions([])
    }
  }, [quiz])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' 
        ? checked 
        : (type === 'number' 
          ? (name === 'maxAttempts' && (value === '' || value === null)) 
            ? null 
            : (parseFloat(value) || (name === 'maxAttempts' ? null : 0))
          : value)
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleQuestionChange = (e) => {
    const { name, value, type } = e.target
    setCurrentQuestion(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 1 : value
    }))
  }

  const handleOptionChange = (index, field, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }))
  }

  const handleAddOption = () => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: [...prev.options, { text: '', isCorrect: false, order: prev.options.length + 1 }]
    }))
  }

  const handleRemoveOption = (index) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index).map((opt, i) => ({ ...opt, order: i + 1 }))
    }))
  }

  const handleAddQuestion = () => {
    // Validation
    if (!currentQuestion.question.trim()) {
      toast.error('Veuillez saisir la question')
      return
    }

    // Pour les types qui nécessitent des options
    const needsOptions = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TRUE_FALSE', 'FILL_IN_THE_BLANK', 'NUMERIC', 'MATCHING']
    if (needsOptions.includes(currentQuestion.type)) {
      const validOptions = currentQuestion.options.filter(opt => opt.text.trim())
      if (validOptions.length < 2) {
        toast.error('Au moins 2 options sont requises')
        return
      }
      
      // Vérifier qu'il y a au moins une bonne réponse
      const hasCorrectAnswer = validOptions.some(opt => opt.isCorrect)
      if (!hasCorrectAnswer) {
        toast.error('Au moins une option doit être marquée comme correcte')
        return
      }

      // Pour SINGLE_CHOICE et TRUE_FALSE, exactement 1 bonne réponse
      if ((currentQuestion.type === 'SINGLE_CHOICE' || currentQuestion.type === 'TRUE_FALSE') && 
          validOptions.filter(opt => opt.isCorrect).length !== 1) {
        toast.error('Une seule option doit être marquée comme correcte pour ce type de question')
        return
      }
    }

    // Si on édite une question existante
    if (editingQuestionIndex !== null) {
      const updatedQuestion = {
        ...currentQuestion,
        order: editingQuestionIndex + 1,
        options: currentQuestion.options.filter(opt => opt.text.trim())
      }
      setQuestions(prev => prev.map((q, i) => i === editingQuestionIndex ? updatedQuestion : q))
      setEditingQuestionIndex(null)
    } else {
      // Ajouter la question à la liste
      const newQuestion = {
        ...currentQuestion,
        order: questions.length + 1,
        options: currentQuestion.options.filter(opt => opt.text.trim())
      }
      setQuestions(prev => [...prev, newQuestion])
    }
    
    // Réinitialiser et fermer le modal
    resetQuestionForm()
    setIsQuestionModalOpen(false)
    toast.success(editingQuestionIndex !== null ? 'Question modifiée avec succès' : 'Question ajoutée avec succès')
  }

  const resetQuestionForm = () => {
    setCurrentQuestion({
      question: '',
      type: 'SINGLE_CHOICE',
      points: 1,
      order: questions.length + 1,
      isRequired: true,
      options: [
        { text: '', isCorrect: false, order: 1 },
        { text: '', isCorrect: false, order: 2 }
      ]
    })
    setEditingQuestionIndex(null)
  }

  const handleOpenQuestionModal = (index = null) => {
    if (index !== null) {
      // Éditer une question existante
      const question = questions[index]
      setCurrentQuestion({
        ...question,
        options: question.options && question.options.length > 0 
          ? question.options 
          : [
              { text: '', isCorrect: false, order: 1 },
              { text: '', isCorrect: false, order: 2 }
            ]
      })
      setEditingQuestionIndex(index)
    } else {
      // Nouvelle question
      resetQuestionForm()
    }
    setIsQuestionModalOpen(true)
  }

  const handleCloseQuestionModal = () => {
    setIsQuestionModalOpen(false)
    resetQuestionForm()
  }

  const handleRemoveQuestion = (index) => {
    setQuestions(prev => prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i + 1 })))
  }

  const handleQuestionMoveUp = (index) => {
    if (index === 0) return
    setQuestions(prev => {
      const newQuestions = [...prev]
      const temp = newQuestions[index]
      newQuestions[index] = { ...newQuestions[index - 1], order: index + 1 }
      newQuestions[index - 1] = { ...temp, order: index }
      return newQuestions
    })
  }

  const handleQuestionMoveDown = (index) => {
    if (index >= questions.length - 1) return
    setQuestions(prev => {
      const newQuestions = [...prev]
      const temp = newQuestions[index]
      newQuestions[index] = { ...newQuestions[index + 1], order: index + 1 }
      newQuestions[index + 1] = { ...temp, order: index + 2 }
      return newQuestions
    })
  }

  const handleOptionMoveUp = (questionIndex, optionIndex) => {
    if (optionIndex === 0) return
    setCurrentQuestion(prev => {
      const newOptions = [...prev.options]
      const temp = newOptions[optionIndex]
      newOptions[optionIndex] = { ...newOptions[optionIndex - 1], order: optionIndex + 1 }
      newOptions[optionIndex - 1] = { ...temp, order: optionIndex }
      return { ...prev, options: newOptions }
    })
  }

  const handleOptionMoveDown = (questionIndex, optionIndex) => {
    if (optionIndex >= currentQuestion.options.length - 1) return
    setCurrentQuestion(prev => {
      const newOptions = [...prev.options]
      const temp = newOptions[optionIndex]
      newOptions[optionIndex] = { ...newOptions[optionIndex + 1], order: optionIndex + 1 }
      newOptions[optionIndex + 1] = { ...temp, order: optionIndex + 2 }
      return { ...prev, options: newOptions }
    })
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est obligatoire'
    }
    if (formData.timeLimit !== null && formData.timeLimit < 0) {
      newErrors.timeLimit = 'La limite de temps doit être positive'
    }
    if (formData.passingScore < 0 || formData.passingScore > 100) {
      newErrors.passingScore = 'Le score de passage doit être entre 0 et 100'
    }
    // maxAttempts peut être null (illimité) ou un nombre >= 1
    if (formData.maxAttempts !== null && formData.maxAttempts !== undefined && formData.maxAttempts < 1) {
      newErrors.maxAttempts = 'Le nombre de tentatives doit être au moins 1, ou laissez vide pour illimité'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const createQuestions = async (quizId) => {
    if (questions.length === 0) {
      console.log('Aucune question à créer')
      return // Pas de questions à créer
    }

    if (!quizId) {
      console.error('QuizId manquant pour créer les questions')
      toast.error('Erreur: ID du quiz manquant')
      return
    }

    // Filtrer uniquement les nouvelles questions (sans ID) pour éviter de recréer les questions existantes
    const newQuestions = questions.filter(q => !q.id)
    
    if (newQuestions.length === 0) {
      console.log('Aucune nouvelle question à créer (toutes les questions existent déjà)')
      return
    }

    console.log(`Création de ${newQuestions.length} nouvelle(s) question(s) pour le quiz ${quizId}`)
    setCreatingQuestions(true)
    try {
      // Créer chaque nouvelle question via l'API
      for (let i = 0; i < newQuestions.length; i++) {
        const question = newQuestions[i]
        console.log(`Création de la question ${i + 1}/${newQuestions.length}:`, question)
        
        const questionData = {
          question: question.question,
          type: question.type,
          points: question.points,
          order: question.order,
          isRequired: question.isRequired !== false,
          quizId: quizId // L'API attend quizId dans le body
        }

        // Ajouter les options si nécessaire
        const needsOptions = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TRUE_FALSE', 'FILL_IN_THE_BLANK', 'NUMERIC', 'MATCHING']
        if (needsOptions.includes(question.type) && question.options && question.options.length > 0) {
          questionData.options = question.options.map((opt, index) => ({
            text: opt.text,
            isCorrect: opt.isCorrect,
            order: index + 1
          }))
        }

        console.log('Données de la question à envoyer:', questionData)
        console.log('Endpoint:', endpoints.createQuizQuestion(quizId))

        // Utiliser l'endpoint pour créer la question
        const response = await apiClient(endpoints.createQuizQuestion(quizId), 'POST', {
          body: questionData
        })
        
        console.log(`Question ${i + 1} créée avec succès:`, response)
      }
      
      toast.success(`${newQuestions.length} question(s) créée(s) avec succès !`)
      
      // Appeler le callback après la création des questions
      if (onQuestionsCreated) {
        onQuestionsCreated()
      }
    } catch (error) {
      console.error('Error creating questions:', error)
      toast.error('Erreur lors de la création des questions')
      throw error
    } finally {
      setCreatingQuestions(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setSaving(true)
    try {
      // Préparer les données selon le format attendu par l'API
      const quizData = {
        title: formData.title,
        description: formData.description || "",
        instructions: formData.instructions || "Répondez à toutes les questions.",
        timeLimit: formData.timeLimit ? parseInt(formData.timeLimit) : null,
        passingScore: parseInt(formData.passingScore) || 70,
        maxAttempts: formData.maxAttempts || null, // null = tentatives illimitées
        isRandomized: formData.isRandomized || false,
        isPublished: formData.isPublished !== undefined ? formData.isPublished : true
      }
      
      // Si c'est un quiz existant, utiliser l'API PUT /quiz/:id/complete pour mettre à jour le quiz complet
      if (quiz && quiz.id) {
        // Fonction helper pour vérifier si un type de question nécessite des options
        const questionNeedsOptions = (type) => {
          return ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TRUE_FALSE', 'FILL_IN_THE_BLANK', 'NUMERIC', 'MATCHING'].includes(type)
        }
        
        // Préparer les questions avec leurs options au format attendu par l'API
        const questionsData = questions.map(q => {
          const questionData = {
            ...(q.id ? { id: q.id } : {}), // Inclure l'ID seulement si la question existe
            question: q.question || '',
            type: q.type || 'SINGLE_CHOICE',
            points: q.points || 1,
            order: q.order || 1,
            isRequired: q.isRequired !== undefined ? q.isRequired : true,
            ...(questionNeedsOptions(q.type) && q.options && q.options.length > 0 ? {
              options: q.options
                .filter(opt => opt.text && opt.text.trim()) // Filtrer les options vides
                .map(opt => ({
                  ...(opt.id ? { id: opt.id } : {}), // Inclure l'ID seulement si l'option existe
                  text: opt.text.trim(),
                  isCorrect: opt.isCorrect || false,
                  order: opt.order || 1
                }))
            } : {})
          }
          return questionData
        })

        // Préparer les données complètes pour l'API
        const completeQuizData = {
          ...quizData,
          questions: questionsData
        }

        // Utiliser l'API PUT /quiz/:id/complete pour mettre à jour le quiz complet
        const updatedQuiz = await apiClient(endpoints.quizComplete(quiz.id), 'PUT', {
          body: completeQuizData
        })

        toast.success('Quiz mis à jour avec succès !')
        
        // Appeler le callback pour recharger les données
        if (onQuestionsCreated) {
          onQuestionsCreated()
        }
        
        return updatedQuiz
      } else {
        // Pour un nouveau quiz, utiliser la méthode existante
        console.log('Appel de onSave avec quizData:', quizData)
        const savedQuiz = await onSave(quizData)
        console.log('onSave a retourné:', savedQuiz)
        
        // Obtenir l'ID du quiz
        const quizId = savedQuiz?.id || savedQuiz?.data?.id || (typeof savedQuiz === 'object' && savedQuiz !== null && 'id' in savedQuiz ? savedQuiz.id : null)
        
        console.log('Quiz sauvegardé:', savedQuiz, 'QuizId:', quizId, 'Questions totales:', questions.length)
        
        // Filtrer les nouvelles questions (sans ID) pour les créer
        const newQuestions = questions.filter(q => !q.id)
        
        if (quizId && newQuestions.length > 0) {
          console.log(`Création de ${newQuestions.length} nouvelle(s) question(s) pour le quiz:`, quizId)
          await createQuestions(quizId)
          // Le callback onQuestionsCreated sera appelé dans createQuestions après la création
        } else if (quizId && newQuestions.length === 0) {
          console.log('Aucune nouvelle question à créer')
          // Si aucune question à créer, appeler le callback quand même pour recharger
          if (onQuestionsCreated) {
            onQuestionsCreated()
          }
        } else if (!quizId) {
          console.log('QuizId non trouvé ou quiz non créé')
          toast.error('Erreur: Impossible de récupérer l\'ID du quiz')
        }
      }
    } catch (error) {
      console.error('Error saving quiz:', error)
      toast.error(error?.response?.data?.message || 'Erreur lors de la sauvegarde du quiz')
    } finally {
      setSaving(false)
    }
  }

  const needsOptions = (type) => {
    return ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TRUE_FALSE', 'FILL_IN_THE_BLANK', 'NUMERIC', 'MATCHING'].includes(type)
  }

  return (
    <>
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {quiz ? 'Modifier le quiz' : 'Nouveau Quiz'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {quiz ? 'Modifiez les informations du quiz' : 'Créez un nouveau quiz pour votre module'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {quiz && onShowTranslations && (
              <Button
                variant="outline"
                size="sm"
                onClick={onShowTranslations}
                className="h-8"
                title="Gérer les traductions du quiz"
              >
                <Languages className="w-4 h-4 mr-2" />
                Traductions
              </Button>
            )}
            {onCancel && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                className="h-8"
              >
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-6">
          {/* Quiz Info Section */}
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Informations du quiz</h3>
            
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ex: Quiz de fin de module"
                required
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Description du quiz..."
              />
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instructions
              </label>
              <textarea
                name="instructions"
                value={formData.instructions}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Instructions pour les étudiants..."
              />
            </div>

            {/* Settings */}
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Limite de temps (min)
                  </label>
                  <input
                    type="number"
                    name="timeLimit"
                    value={formData.timeLimit || ''}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                      errors.timeLimit ? 'border-red-500' : 'border-gray-300'
                    }`}
                    min="0"
                    placeholder="30 (laisser vide = pas de limite)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Laisser vide pour aucune limite de temps
                  </p>
                  {errors.timeLimit && (
                    <p className="mt-1 text-sm text-red-600">{errors.timeLimit}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Award className="w-4 h-4" />
                    Score de passage (%)
                  </label>
                  <input
                    type="number"
                    name="passingScore"
                    value={formData.passingScore}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                      errors.passingScore ? 'border-red-500' : 'border-gray-300'
                    }`}
                    min="0"
                    max="100"
                    placeholder="70"
                  />
                  {errors.passingScore && (
                    <p className="mt-1 text-sm text-red-600">{errors.passingScore}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tentatives max
                  </label>
                  <input
                    type="number"
                    name="maxAttempts"
                    value={formData.maxAttempts ?? ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    min="1"
                    placeholder="Laissez vide pour illimité"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Nombre maximum de tentatives. Laissez vide pour permettre des tentatives illimitées.
                  </p>
                  {errors.maxAttempts && (
                    <p className="mt-1 text-xs text-red-600">{errors.maxAttempts}</p>
                  )}
                </div>
              </div>

              {/* Options supplémentaires */}
              <div className="pt-4 border-t border-gray-200 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isRandomized"
                    checked={formData.isRandomized}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                  />
                  <label className="text-sm text-gray-700">
                    Questions aléatoires
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                  />
                  <label className="text-sm text-gray-700">
                    Publié (visible pour les étudiants)
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Questions Section */}
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-semibold text-gray-900">Questions ({questions.length})</h3>
            </div>

            {/* Questions existantes */}
            {questions.length > 0 && (
              <div className="space-y-4">
                {questions.map((q, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-700">Question {index + 1}</span>
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                            {QUESTION_TYPES.find(t => t.value === q.type)?.label || q.type}
                          </span>
                          <span className="text-xs text-gray-500">
                            {q.points} point{q.points > 1 ? 's' : ''}
                          </span>
                        </div>
                        <p className="text-gray-900 font-medium mb-2">{q.question}</p>
                        {needsOptions(q.type) && q.options && q.options.length > 0 && (
                          <div className="space-y-1 mt-2">
                            {q.options.map((opt, optIndex) => (
                              <div key={optIndex} className="flex items-center gap-2 text-sm">
                                <input
                                  type={q.type === 'MULTIPLE_CHOICE' ? 'checkbox' : 'radio'}
                                  checked={opt.isCorrect}
                                  readOnly
                                  className="text-green-600"
                                />
                                <span className={opt.isCorrect ? 'text-green-700 font-medium' : 'text-gray-600'}>
                                  {opt.text}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleQuestionMoveUp(index)}
                          disabled={index === 0}
                          className={`p-1.5 rounded transition-colors ${
                            index === 0
                              ? 'opacity-30 cursor-not-allowed text-gray-400'
                              : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                          }`}
                          title="Monter"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleQuestionMoveDown(index)}
                          disabled={index >= questions.length - 1}
                          className={`p-1.5 rounded transition-colors ${
                            index >= questions.length - 1
                              ? 'opacity-30 cursor-not-allowed text-gray-400'
                              : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                          }`}
                          title="Descendre"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenQuestionModal(index)}
                          className="text-blue-600 hover:text-blue-800 p-1.5 rounded transition-colors"
                          title="Modifier"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveQuestion(index)}
                          className="text-red-600 hover:text-red-800 p-1.5 rounded transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bouton pour ouvrir le modal d'ajout de question */}
            <div className="flex justify-center">
              <Button
                type="button"
                onClick={() => handleOpenQuestionModal()}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une question
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving || creatingQuestions}
          >
            Annuler
          </Button>
        )}
        <Button
          type="submit"
          onClick={handleSubmit}
          disabled={saving || creatingQuestions}
          className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
        >
          {saving || creatingQuestions ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {creatingQuestions ? 'Création des questions...' : (quiz ? 'Modification...' : 'Création...')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {quiz ? 'Modifier' : 'Créer le quiz'}
            </>
          )}
        </Button>
      </div>
    </div>

    {/* Modal pour ajouter/éditer une question */}
    {isQuestionModalOpen && (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={handleCloseQuestionModal}
      >
        <div 
          className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {editingQuestionIndex !== null ? 'Modifier la question' : 'Ajouter une question'}
            </h3>
            <button
              onClick={handleCloseQuestionModal}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Formulaire de question */}
          <div className="space-y-4">
            
            {/* Type de question */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de question <span className="text-red-500">*</span>
              </label>
              <select
                name="type"
                value={currentQuestion.type}
                onChange={(e) => {
                  const newType = e.target.value
                  setCurrentQuestion(prev => ({
                    ...prev,
                    type: newType,
                    options: needsOptions(newType) 
                      ? (newType === 'TRUE_FALSE' 
                          ? [
                              { text: 'Vrai', isCorrect: true, order: 1 },
                              { text: 'Faux', isCorrect: false, order: 2 }
                            ]
                          : [
                              { text: '', isCorrect: false, order: 1 },
                              { text: '', isCorrect: false, order: 2 }
                            ])
                      : []
                  }))
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
              >
                {QUESTION_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {/* Question */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question <span className="text-red-500">*</span>
              </label>
              <textarea
                name="question"
                value={currentQuestion.question}
                onChange={handleQuestionChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Saisissez votre question..."
              />
            </div>

            {/* Points */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Points <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="points"
                value={currentQuestion.points}
                onChange={handleQuestionChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
              />
            </div>

            {/* Options (si nécessaire) */}
            {needsOptions(currentQuestion.type) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Options de réponse <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {currentQuestion.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type={currentQuestion.type === 'MULTIPLE_CHOICE' ? 'checkbox' : 'radio'}
                        checked={option.isCorrect}
                        onChange={(e) => {
                          if (currentQuestion.type === 'SINGLE_CHOICE' || currentQuestion.type === 'TRUE_FALSE') {
                            // Pour SINGLE_CHOICE et TRUE_FALSE, une seule option correcte
                            setCurrentQuestion(prev => ({
                              ...prev,
                              options: prev.options.map((opt, i) => ({
                                ...opt,
                                isCorrect: i === index ? e.target.checked : false
                              }))
                            }))
                          } else {
                            // Pour MULTIPLE_CHOICE, plusieurs options peuvent être correctes
                            handleOptionChange(index, 'isCorrect', e.target.checked)
                          }
                        }}
                        className="text-green-600"
                      />
                      <input
                        type="text"
                        value={option.text}
                        onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                        placeholder={`Option ${index + 1}`}
                      />
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleOptionMoveUp(null, index)}
                          disabled={index === 0}
                          className={`p-1.5 rounded transition-colors ${
                            index === 0
                              ? 'opacity-30 cursor-not-allowed text-gray-400'
                              : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                          }`}
                          title="Monter"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOptionMoveDown(null, index)}
                          disabled={index >= currentQuestion.options.length - 1}
                          className={`p-1.5 rounded transition-colors ${
                            index >= currentQuestion.options.length - 1
                              ? 'opacity-30 cursor-not-allowed text-gray-400'
                              : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                          }`}
                          title="Descendre"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                        {currentQuestion.options.length > 2 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveOption(index)}
                            className="text-red-600 hover:text-red-800 p-1.5 rounded transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddOption}
                    className="flex items-center gap-2 text-sm text-[#141835] hover:text-[#1a1f4a]"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter une option
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {currentQuestion.type === 'MULTIPLE_CHOICE' 
                    ? 'Cochez toutes les bonnes réponses'
                    : currentQuestion.type === 'TRUE_FALSE'
                    ? 'Sélectionnez Vrai ou Faux comme bonne réponse'
                    : 'Sélectionnez la bonne réponse'}
                </p>
              </div>
            )}

            {/* Bouton ajouter/modifier question */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseQuestionModal}
              >
                Annuler
              </Button>
              <Button
                type="button"
                onClick={handleAddQuestion}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                {editingQuestionIndex !== null ? (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Modifier la question
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter la question
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
